import React, { useEffect, useState } from "react";
import { cqAPI, Auth } from "../../lib/Auth";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  VStack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  Checkbox,
  useColorModeValue
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCcVisa,
  faCcMastercard,
  faCcAmex,
  faCcDiscover,
} from "@fortawesome/free-brands-svg-icons";
import dialogManager from "../DialogManager/DialogManager";
import EditableText from "../EditableText/EditableText";
import moment from 'moment';

function Subscription() {
  const [editMode, setEditMode] = useState(false);
  const [stagedChange, setStagedChange] = useState(null);
  const [availableBundles, setAvailableBundles] = useState([]);
  const [activeBundle, setActiveBundle] = useState(null);
  const [displayedBundle, setDisplayedBundle] = useState(null);
  const [card, setCard] = useState({});
  const [stagedChanges, setStagedChanges] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [scheduledBundle, setScheduledBundle] = useState({});
  const [currentUsages, setCurrentUsages] = useState([]);
  const [existingCurrentUsages, setExistingCurrentUsages] = useState([]);
  const [activeAddons, setActiveAddons] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const headerBackgroundColor = useColorModeValue("#F1F1F1", "#303343");
  const textColor = useColorModeValue("black", "white");
  const [isPartner, setIsPartner] = useState(false);
  const [trialDays, setTrialDays] = useState("");
  const [sendInvoice, setSendInvoice] = useState(false);
  const [subscription, setSubscription] = useState({});

  const [endTrialLoading, setEndTrialLoading] = useState(false);

  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  // MULTI PROMO: array of codes for the modal
  const [promotionCodes, setPromotionCodes] = useState([""]);
  const [promotionCallback, setPromotionCallback] = useState(null);
  const [ignoreLimit, setIgnoreLimit] = useState([])
  
  const isLoadedPartner = window.location.hostname.includes("adec") || window.location.hostname.includes("a-dec");

  const [subscriptionStartDate, setSubscriptionStartDate] = useState("");
  const [requiresPaymentMethod, setRequiresPaymentMethod] = useState(false);

  const toast = useToast();

  const fetchAddons = async () => {
    const response = await cqAPI.get("/settings/subscriptions/addons");
    setAvailableAddons(response.data?.addons || []);
    setActiveAddons(response.data?.active_addons || []);
  };

  const fetchBundles = async () => {
    const response = await cqAPI.get("/settings/subscriptions/bundles");
    setAvailableBundles(response.data?.bundles || []);
    setActiveBundle(response.data?.active_bundle || null);
    setScheduledBundle(response.data?.scheduled);
    setDisplayedBundle(response.data?.active_bundle || null);
    setIgnoreLimit(response.data?.ignoreLimit || [])
  };

  const fetchPlans = async () => {
    const response = await cqAPI.get("/settings/subscriptions");
    setAvailableProducts(response.data?.availableProducts);
    setCurrentUsages(response.data?.currentUsages);
    setExistingCurrentUsages(response.data?.currentUsages);
    setCard(response.data?.paymentMethod || {});
    setSubscription(response.data?.subscription);
    // Normalize promotions to array for the modal
    const promos = response.data?.promotion;
    setPromotionCodes(
      Array.isArray(promos)
        ? (promos.length ? promos : [""])
        : (promos ? [promos] : [""])
    );
  };

  useEffect(() => {
    fetchPlans().then(() => {
      fetchBundles();
      fetchAddons();
    });
    cqAPI.get('/isloggedin').then(data=>{
      setIsPartner(data?.data?.user?.business?.partner?.enable_start_date ? true : false);
    });
  }, []);

  const toggleEditMode = () => {
    setEditMode(!editMode);
    if (!editMode) {
      setStagedChange(null);
      setDisplayedBundle(activeBundle);
    }
  };

  const stageChange = (bundle_id) => {
    setStagedChange(bundle_id);
    setDisplayedBundle(bundle_id);
  };

  const openPromotionModal = (callback) => {
    setPromotionCallback(() => callback);
    setIsPromotionModalOpen(true);
  };

  const handleSaveChanges = async () => {
    if (activeBundle == null && stagedChange != null) {
      openPromotionModal((enteredPromotionCodes, selectedStartDate, doInvoice, days) => {
        if (enteredPromotionCodes === null && selectedStartDate === null) {
          return;
        }
        saveChanges(enteredPromotionCodes, selectedStartDate, doInvoice, days);
      });
    } else {
      // Always send promotions as array (empty when none)
      saveChanges([], null);
    }
  };

  // Always include `promotion` as array (empty if none)
  const saveChanges = async (promotionCodesArg = [], startDate, doInvoice, days) => {
    const simplifiedChanges = simplifyStagedChanges();

    const codes = Array.isArray(promotionCodesArg)
      ? promotionCodesArg.map((c) => (c || "").trim()).filter(Boolean)
      : [];

    const response = await cqAPI.put(
      `/settings/subscriptions/batch-update?bundle_id=${stagedChange || ""}`,
      {
        changes: simplifiedChanges,
        promotion: codes, // array, possibly empty
        ...(startDate
          ? { start_date: startDate }
          : {}
        ),
        ...(doInvoice
          ? { invoiced: true }
          : {}
        ),
        ...(days
          ? { trial_period_days: days }
          : {}
        )
      }
    );

    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    } else {
      fetchPlans().then(() => {
        fetchBundles();
        fetchAddons();
      });
      setStagedChange(null);
      setStagedChanges([]);
      setEditMode(false);
      setActiveBundle(stagedChange);
      setStagedChange(null);
    }
  };

  const requestCreditCardChange = async () => {
    const response = await cqAPI.requestCreditCard({}).catch((error) => {
      console.error(error);
    });
    if (response) {
      setCard(response);
    }
  };

  // Accept string or array; always POST array when applying directly
  const applyPromotionCode = async (promotionInput) => {
    const arr = Array.isArray(promotionInput)
      ? promotionInput.map((s) => String(s || "").trim()).filter(Boolean)
      : (promotionInput ? [String(promotionInput).trim()] : []);
    if (!arr.length) {
      return;
    }
    const response = await cqAPI.post("/settings/subscription/promotion", {
      promotion: arr,
    });
    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    } else {
      toast({
        title: "Promotion Applied",
        description: "Your promotion has been successfully applied.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const cardBrandIcon = (brand) => {
    switch (brand) {
      case "Visa":
        return faCcVisa;
      case "MasterCard":
        return faCcMastercard;
      case "American Express":
        return faCcAmex;
      case "Discover":
        return faCcDiscover;
      default:
        return faCreditCard;
    }
  };

  const stageBatchChange = (change) => {
    setStagedChanges((prev) => [...prev, change]);
    const product = availableProducts.find((p) => p.id === change.product_id);

    setCurrentUsages((prevCurrentUsages) => {
      const isAlreadySubscribedToAnotherPlan = prevCurrentUsages.find((u) =>
        product.plans.find((p) => p.id === u.plan)
      );

      let updatedUsages;
      let newPlan;
      switch (change.type) {
        case "subscribe":
          newPlan = product.plans.find((p) => p.id === change.plan_id);
          if (isAlreadySubscribedToAnotherPlan) {
            updatedUsages = [
              ...prevCurrentUsages.filter(
                (x) => x._id !== isAlreadySubscribedToAnotherPlan._id
              ),
              {
                ...isAlreadySubscribedToAnotherPlan,
                sub_id: undefined,
                sub_item_id: undefined,
                plan: change.plan_id,
                usage_type: newPlan.usage_type,
                _id: `temp-${Math.random().toString()}`,
                isNew: true,
                limit: newPlan.limit
              },
            ];
          } else {
            updatedUsages = [
              ...prevCurrentUsages,
              {
                plan: change.plan_id,
                usage_type: newPlan?.usage_type,
                _id: `temp-${Math.random().toString()}`,
                amount: 1,
                isNew: true,
                limit: newPlan?.limit
              },
            ];
          }
          break;
        case "changeLicenses":
          updatedUsages = [
            ...prevCurrentUsages.filter(
              (x) => x._id !== isAlreadySubscribedToAnotherPlan._id
            ),
            {
              ...isAlreadySubscribedToAnotherPlan,
              amount: change.amount,
            },
          ];
          break;
        case "delete":
          updatedUsages = prevCurrentUsages.filter(
            (u) => u._id !== change.usage_id
          );
          break;
        default:
          updatedUsages = prevCurrentUsages;
          break;
      }

      return updatedUsages;
    });
  };

  useEffect(() => {
    setCurrentUsages(existingCurrentUsages);

    if (activeBundle === displayedBundle) {
      return;
    }
    if (!displayedBundle) {
      existingCurrentUsages.forEach((usage) => {
        const product = availableProducts.find((p) =>
          p.plans.find((plan) => plan.id === usage.plan)
        );
        stageBatchChange({
          type: "delete",
          product_id: product.id,
          plan_id: usage.plan,
          usage_id: usage._id,
        });
      });
      return;
    }

    const selectedBundlePlan = availableBundles.find(
      (b) => b._id === displayedBundle
    );
    selectedBundlePlan.items.forEach((item) => {
      stageBatchChange({
        type: "subscribe",
        product_id: item[0].id,
        plan_id: item[1].id,
      });
    });

    existingCurrentUsages.forEach((usage) => {
      const product = availableProducts.find((p) =>
        p.plans.find((plan) => plan.id === usage.plan)
      );
      const toBeAdded = selectedBundlePlan.items.find(
        (item) => item[1].id === usage.plan
      );
      if (!toBeAdded) {
        stageBatchChange({
          type: "delete",
          product_id: product.id,
          plan_id: usage.plan,
          usage_id: usage._id,
        });
      }
    });
  }, [displayedBundle]);

  function simplifyStagedChanges() {
    const changes = currentUsages.reduce((acc, usage) => {
      const existing = existingCurrentUsages.find((p) => p.plan === usage.plan);
      if (!existing) {
        acc.push({ ...usage, type: "subscribe" });
      } else if (existing.amount !== usage.amount) {
        acc.push({ ...usage, type: "changeLicenses" });
      }
      return acc;
    }, []);

    const deleted = existingCurrentUsages.reduce((acc, usage) => {
      const stillExists = currentUsages.find((p) => p.plan === usage.plan);
      if (!stillExists) {
        acc.push({ ...usage, type: "delete" });
      }
      return acc;
    }, []);

    return [...deleted, ...changes];
  }

  const formatTieredPricing = (
    tiers,
    tiers_mode,
    unit,
    usage_type,
    fallbackPrice = 0
  ) => {
    if (isLoadedPartner && usage_type === "licensed") {
      return null;
    }
    if (!tiers || tiers.length === 0) {
      const price = (fallbackPrice / 100).toFixed(3);
      const perUnitLabel = unit ? `/${unit}` : "";
      const licenseSuffix = usage_type === "licensed" ? " per month" : "";
      return (
        <Text>
          Price: ${price}
          {perUnitLabel}
          {licenseSuffix}
        </Text>
      );
    }

    return tiers.map((tier, index) => {
      let costParts = [];
      const hasFlat = tier.flat_amount_decimal > 0;
      const hasUnit = tier.unit_amount_decimal > 0;

      if (hasFlat) {
        costParts.push(`$${(tier.flat_amount_decimal / 100).toFixed(2)}`);
      }

      if (hasUnit) {
        const perUnitLabel = unit ? `/${unit}` : "";
        const licenseSuffix = usage_type === "licensed" ? " per month" : "";
        costParts.push(
          `$${(tier.unit_amount_decimal / 100).toFixed(3)}${perUnitLabel}${licenseSuffix}`
        );
      }

      if (!hasFlat && !hasUnit) {
        costParts.push("$0.00");
      }

      const priceString = isLoadedPartner && (index === 0) ? 'included' : costParts.join(" + ");

      if (tier.up_to === null) {
        return (
          <Text key={index}>
            Anything more or equal than {tiers[index - 1]?.up_to || 0}{" "}
            {unit || "units"}: {priceString}
          </Text>
        );
      } else {
        return (
          <Text key={index}>
            Tier {index + 1} between {tiers[index - 1]?.up_to || 0} and{" "}
            {tier.up_to} {unit || "units"}: {priceString}
          </Text>
        );
      }
    });
  };

  const changeLicenses = async (product_id, plan_id, usage_id, amount) => {
    const response = await cqAPI.put("/settings/subscriptions", {
      product_id,
      plan_id,
      usage_id,
      amount,
    });

    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return false;
    }

    await fetchPlans().then(() => fetchBundles());
    return true;
  };

  const getUsageForLicense = (product_id) => {
    const product = availableProducts.find((p) => p.id === product_id);
    const existing = existingCurrentUsages.find((usage) =>
      product.plans.find((pln) => pln.id === usage.plan)
    );
    return {
      amount: existing ? existing.amount : 1,
      existing_id: existing?._id,
    };
  };

  const subscribeAddon = async (addon_id) => {
    const response = await cqAPI.post(`/settings/subscriptions/${addon_id}`);
    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    }
    await fetchAddons();
  };

  const removeAddon = async (addon_id) => {
    const response = await cqAPI.del(`/settings/subscriptions/${addon_id}`);
    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    }
    await fetchAddons();
  };

  const UnscheduleActivation = async () => {
    await cqAPI.del('/settings/subscriptions/bundle-activation');
    fetchBundles();
  };

  const EndTrial = async () => {
    try {
      setEndTrialLoading(true);
      const response = await cqAPI.post('/settings/subscriptions/end-trial');
      if (response?.type === 'ok'){
        await fetchPlans();
      } else if (response?.type === 'error'){
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setEndTrialLoading(false);
    }
  };

  const isValidFutureDate = (maybeDateString) => {
    if (!maybeDateString) {
      return true;
    }
    const selected = moment(maybeDateString, 'YYYY-MM-DD').toDate();
    const now = new Date();
    return selected >= now;
  };

  const handleModalCloseOrCancel = () => {
    setTrialDays("");
    setSendInvoice(false);
    setIsPromotionModalOpen(false);
    setPromotionCodes([""]);
    setSubscriptionStartDate("");
    if (promotionCallback) {
      promotionCallback(null, null);
      setPromotionCallback(null);
    }
  };

  const handleModalContinue = () => {
    if (!isValidFutureDate(subscriptionStartDate)) {
      toast({
        title: "Invalid Date",
        description: "Please select a date strictly in the future, or leave it empty.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsPromotionModalOpen(false);
    // Trim & filter empties => [] when none
    const codes = promotionCodes.map(c => (c || "").trim()).filter(Boolean);
    const date = subscriptionStartDate;
    const doInvoice = sendInvoice;
    const days = trialDays;
    setPromotionCodes([""]);
    setSubscriptionStartDate("");
    if (promotionCallback) {
      promotionCallback(codes, date, doInvoice, days);
      setPromotionCallback(null);
    }
  };

  function CreditCardDisplay({ last4, brand }) {
    return (
      <Flex align="center">
        <FontAwesomeIcon icon={cardBrandIcon(brand)} />
        <Flex align="center" justify="center">
          {[...Array(12)].map((_, i) => (
            <FontAwesomeIcon
              key={i}
              icon={faCircle}
              style={{
                fontSize: "6px",
                paddingLeft: i % 4 === 0 && i !== 0 ? "10px" : "1px",
              }}
            />
          ))}
        </Flex>{" "}
        <Text>{last4}</Text>
      </Flex>
    );
  }
  
  const removeLimit = async (plan)=>{
    const response = await cqAPI.del('/settings/subscriptions/remove-limit',{
      plan_id : plan.id
    })
    if (response.type === 'ok'){
      setIgnoreLimit([...ignoreLimit, plan.id])
    }
  }

  // NEW: remove one promo row (keep at least one input visible)
  const removePromotionAt = (idx) => {
    setPromotionCodes((prev) => {
      if (prev.length === 1) return [""];
      const next = prev.slice(0, idx).concat(prev.slice(idx + 1));
      return next.length ? next : [""];
    });
  };

  useEffect(() => {
    if (requiresPaymentMethod) {
      requestCreditCardChange()
        .catch(err => console.error('Cannot request credit card:', err))
        .finally(() => setRequiresPaymentMethod(false));
    }
  }, [requiresPaymentMethod]);

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="right" padding="0px 0px 20px 0px">
        <Button
          marginLeft="20px"
          leftIcon={<CreditCardDisplay last4={card.last4} brand={card.brand} />}
          onClick={requestCreditCardChange}
        >
          {!card.last4 && <Text>Add Credit Card</Text>}
        </Button>
        {(Auth.user.isSuperAdmin || !isPartner) && !scheduledBundle?.bundle && (
          <Button
            marginLeft="10px"
            onClick={() =>
              openPromotionModal((enteredPromotionCodes, _selectedDate) => {
                if (enteredPromotionCodes && enteredPromotionCodes.length) {
                  applyPromotionCode(enteredPromotionCodes);
                }
              })
            }
          >
            Apply Promotion
          </Button>
        )}
      </Flex>

      <Tabs variant="enclosed">
        <TabList>
          <Tab>Subscription</Tab>
          {(Auth.user.isSuperAdmin || !isPartner) && (<Tab>Add-ons</Tab>)}
        </TabList>

        <TabPanels>
          <TabPanel>
            {(Auth.user.isSuperAdmin || Auth.user.isPartnerAdmin) && (<Flex justifyContent="right" padding="0px 0px 20px 0px">
              {!scheduledBundle?.bundle && (
                <Button onClick={toggleEditMode}>
                  {editMode ? "Cancel Changes" : "Change Plan"}
                </Button>
              )}
              {editMode && (
                <Button ml={4} colorScheme="teal" onClick={handleSaveChanges}>
                  Save Changes
                </Button>
              )}
              </Flex>)}

            <SimpleGrid columns={{ base: 1, md: 1, lg: 1 }} spacing="20px">
              {availableBundles
                .filter(
                  (bundle) =>
                    !scheduledBundle?.bundle ||
                    scheduledBundle?.bundle === bundle._id
                )
                .map((bundle, index) => (
                  <Box
                    key={index}
                    borderWidth="1px"
                    borderRadius="lg"
                    mb={4}
                    width="100%"
                  >
                    <Box
                      color={textColor}
                      p={4}
                      bg={headerBackgroundColor}
                      borderTopRadius="lg"
                      position="relative"
                    >
                      <Text>{bundle.name}</Text>
                      <Text>{bundle.description}</Text>
                      {displayedBundle === bundle._id && (
                        <Text color="green.500">
                          Selected {!bundle.active && "(grandfathered)"}
                        </Text>
                      )}

                      {displayedBundle === bundle._id &&
                        subscription.status === "trialing" &&
                        Auth.user &&
                        Auth.user.isSuperAdmin && (
                          <Flex align="center" justify="center">
                            <Text color="green.500" mr={2}>
                              Trial ends on{" "}
                              {moment(subscription.trial_end * 1000).format("L")}
                            </Text>
                            <Button
                              onClick={EndTrial}
                              isLoading={endTrialLoading}
                              isDisabled={endTrialLoading}
                              spinnerPlacement="start"
                            >
                              End Trial Now
                            </Button>
                          </Flex>
                        )}

                      {scheduledBundle?.bundle === bundle._id && (
                        <Flex align="center" justify="center">
                          <Text color="green.500" mr={2}>
                            Activate on{" "}
                            {moment(scheduledBundle.date + 3600000).format("L")}
                          </Text>
                          <Button onClick={() => UnscheduleActivation()}>
                            Unsubscribe
                          </Button>
                        </Flex>
                      )}
                    </Box>
                    <Box p={4}>
                      <VStack align="left">
                        {bundle.items.map(([product, plan], itemIndex) => (
                          <Flex justify="space-between" key={itemIndex} mb={4}>
                            <Box flex="1" textAlign="left" ml={4}>
                              <Flex align="center">
                              <Text fontWeight="bold" paddingRight="10px">
                                {product.name}: {!ignoreLimit.includes(plan.id) && plan.metadata && plan.metadata.limit && (`(${plan.metadata.limit} ${product.unit_label}s included)`)}
                              </Text>
                              {!ignoreLimit.includes(plan.id) && plan.metadata && plan.metadata.limit && (<Button id={`remove-limit-${product.name.replace(/\s+/g, '-')}`} onClick={()=>removeLimit(plan)}>Remove Limit</Button>)}</Flex>
                              {plan.recurring?.usage_type !== "licensed" ? (
                                <>
                                  {formatTieredPricing(
                                    plan.tiers,
                                    plan.tiers_mode,
                                    product.unit_label,
                                    plan.usage_type,
                                    plan.unit_amount_decimal ||
                                      plan.amount ||
                                      0
                                  )}
                                </>
                              ) : (
                                <>
                                  {!isLoadedPartner && (
                                    <Text>
                                      Price: $
                                      {(plan.unit_amount_decimal / 100).toFixed(
                                        3
                                      )}
                                      /
                                      {product.unit_label
                                        ? product.unit_label
                                        : "unit"}{" "}
                                      {plan.usage_type === "licensed"
                                        ? "per month"
                                        : ""}
                                      {plan?.metadata?.limit &&
                                        ` (limit: ${plan?.metadata?.limit})`}
                                    </Text>
                                  )}
                                  {activeBundle === bundle._id ? (
                                    <Flex>
                                      <EditableText
                                        initialValue={
                                          getUsageForLicense(product.id).amount
                                        }
                                        onSave={(e) =>
                                          changeLicenses(
                                            product.id,
                                            plan.id,
                                            getUsageForLicense(product.id)
                                              .existing_id,
                                            parseInt(e)
                                          )
                                        }
                                        placeholder="Licenses"
                                        pattern={/[1-9]+\d*$/}
                                        invalid="Only numbers greater than 1 are acceptable."
                                        editable={!isPartner || Auth.user.isSuperAdmin}
                                      />
                                    </Flex>
                                  ) : (
                                    displayedBundle === bundle._id && (
                                      <Flex justify="flex-end" align="center">
                                        {getUsageForLicense(product.id).amount}
                                      </Flex>
                                    )
                                  )}
                                </>
                              )}
                            </Box>
                          </Flex>
                        ))}
                      </VStack>
                      {editMode && displayedBundle !== bundle._id && (
                        <Button
                          id={`subscribe-${bundle.name.replace(/\s+/g, '-').toLowerCase()}`}
                          size="sm"
                          mt={4}
                          onClick={() => stageChange(bundle._id)}
                        >
                          Subscribe
                        </Button>
                      )}
                      {editMode && displayedBundle === bundle._id && (
                        <Button
                          size="sm"
                          mt={4}
                          onClick={() => stageChange(null)}
                        >
                          Unsubscribe
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
            </SimpleGrid>
          </TabPanel>

          {(Auth.user.isSuperAdmin || !isPartner) && (<TabPanel>
            <SimpleGrid columns={{ base: 1, md: 1, lg: 1 }} spacing="20px">
              {availableAddons.map((addon, index) => (
                <Box
                  key={index}
                  borderWidth="1px"
                  borderRadius="lg"
                  mb={4}
                  width="100%"
                >
                  <Box
                    bg="green.500"
                    color="white"
                    p={4}
                    borderTopRadius="lg"
                    position="relative"
                  >
                    <Text>{addon.name}</Text>
                    <Text>{addon.description}</Text>
                    {activeAddons.includes(addon._id) && (
                      <Text color="green.500">Active</Text>
                    )}
                  </Box>
                  <Box p={4}>
                    <VStack align="left">
                      {addon.items.map(([product, plan], itemIndex) => (
                        <Flex justify="space-between" key={itemIndex} mb={4}>
                          <Box flex="1" textAlign="left" ml={4}>
                            <Text fontWeight="bold" paddingRight="10px">
                              {product.name}:
                            </Text>
                            {plan.billing_scheme === "tiered" ? (
                              <>
                                {formatTieredPricing(
                                  plan.tiers,
                                  plan.tiers_mode,
                                  product.unit_label,
                                  plan.usage_type,
                                  plan.unit_amount_decimal || plan.amount || 0
                                )}
                              </>
                            ) : (
                              <>
                                {!isLoadedPartner || plan.usage_type !== "licensed" ? (
                                  <Text>
                                    Price: $
                                    {(
                                      (plan.unit_amount_decimal ||
                                        plan.amount ||
                                        0) / 100
                                    ).toFixed(3)}
                                    /
                                    {product.unit_label
                                      ? product.unit_label
                                      : "unit"}{" "}
                                    {plan.usage_type === "licensed"
                                      ? "per month"
                                      : ""}
                                  </Text>
                                ) : null}
                              </>
                            )}
                          </Box>
                        </Flex>
                      ))}
                    </VStack>
                    {!activeAddons.includes(addon._id) ? (
                      <Button
                        size="sm"
                        mt={4}
                        onClick={() => subscribeAddon(addon._id)}
                      >
                        Subscribe
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        mt={4}
                        onClick={() => removeAddon(addon._id)}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </TabPanel>)}
        </TabPanels>
      </Tabs>

      <Modal isOpen={isPromotionModalOpen} onClose={handleModalCloseOrCancel}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalCloseButton onClick={handleModalCloseOrCancel} />
          <ModalBody>
            <Text mb={2} fontWeight="semibold">
              Promotion Code (optional)
            </Text>

            {/* Multiple promo inputs with Add & Remove */}
            {promotionCodes.map((code, idx) => (
              <Flex key={idx} align="center" mb={idx === promotionCodes.length - 1 ? 0 : 2}>
                <Input
                  placeholder={`Promotion Code ${idx + 1}`}
                  value={code}
                  onChange={(e) => {
                    const next = [...promotionCodes];
                    next[idx] = e.target.value;
                    setPromotionCodes(next);
                  }}
                />
                <Button
                  ml={2}
                  size="sm"
                  variant="ghost"
                  onClick={() => removePromotionAt(idx)}
                  isDisabled={promotionCodes.length === 1 && !promotionCodes[0]}
                >
                  Remove
                </Button>
              </Flex>
            ))}
            <Button
              mt={2}
              size="sm"
              variant="outline"
              onClick={() => setPromotionCodes([...promotionCodes, ""])}
            >
              + Add another
            </Button>

            {Auth.user && Auth.user.isSuperAdmin && isPartner && (
              <>
                <Text mb={2} mt={4} fontWeight="semibold">
                  Subscription Start Date (optional)
                </Text>
                <Input
                  type="date"
                  value={subscriptionStartDate}
                  min={moment().add(1, "days").format("YYYY-MM-DD")}
                  onChange={(e) => setSubscriptionStartDate(e.target.value)}
                  placeholder="Leave empty if you want to start now"
                />
              </>
            )}
            {Auth.user && Auth.user.isSuperAdmin && !isPartner && (
              <>
                <Text mb={2} mt={4} fontWeight="semibold">
                  Number of Trial Days (optional)
                </Text>
                <Input
                  type="number"
                  placeholder="Leave empty or enter a number"
                  value={trialDays}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d+$/.test(val)) {
                      setTrialDays(val);
                    }
                  }}
                />

                <Checkbox
                  mt={3}
                  isChecked={sendInvoice}
                  onChange={(e) => setSendInvoice(e.target.checked)}
                >
                  Send Invoice (won&apos;t charge automatically)
                </Checkbox>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalCloseOrCancel}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleModalContinue}>
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Subscription;
